import React from 'react'
import { clone, includes } from 'lodash'
import { Card, CardActions, CardText, CardTitle } from 'material-ui-legacy/Card'
import Subheader from 'material-ui-legacy/Subheader'
import TextField from 'material-ui-legacy/TextField'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'

import RelationalTextInput from '../UI/RelationalTextInput'
import ValidatedTextInput from '../UI/ValidatedTextInput'
import SaveButton from '../UI/SaveButton'
import CancelButton from '../UI/CancelButton'
import NewServiceCategory from '../ServiceCategories/NewServiceCategory'
import NewFormula from '../Formulas/NewFormula'

import Resource from '../../stores/resource'
const ServiceCategoryStore = Resource('service-categories')
const CostFormulaStore = Resource('cost-formulas')
const RateFormulaStore = Resource('rate-formulas')

/*
Props:
- service: a complete service object
  or
- Service fields: will be inserted in the form when present
- submitCallback: callback function called on form submission. This is *only*
  called when the form is valid and the contents have changed. Receives service
  attributes.
- cancelCallback: callback function for the cancel button.
- changedCallback: callback function called when the form contents have changed.
  Receives true if the contents have changed.
- forceChanged: force the form status to "changed"
*/
class ServiceForm extends React.PureComponent {
  constructor(props) {
    super(props)
    // We have to build the state on a regular variable so
    // React doesn't complain about direct state mutation.
    let initialState = {
      formValid: false,
      formChanged: this.props.forceChanged || false,
      formSubmitted: false
    }

    // We can either receive a single data prop or each field separately
    // To make our lives easier, we unpack included data here.
    if (this.props.service) {
      this.initialData = this.props.service.attributes
    } else {
      this.initialData = clone(this.props)
    }

    this.fields = [
      'serviceCategoryId',
      'serviceCategoryName',
      'serviceCategoryFormattedName',
      'name',
      'description',
      'rate',
      'cost',
      'rateUnit',
      'costFormulaName',
      'costFormulaId',
      'rateFormulaName',
      'rateFormulaId',
      'notes',
      'order'
    ]
    for (let field of this.fields) {
      initialState[field] = {
        value: this.initialData[field] || '',
        valid: true
      }
    }
    this.state = initialState // Et voilà!

    // Events to close relational forms
    this.closeAddServiceCategoryEvent = new Event(
      'closeAddServiceCategoryEvent',
      { bubbles: true }
    )
    this.closeAddCostFormulaEvent = new Event('closeAddCostFormulaEvent', {
      bubbles: true
    })
    this.closeAddRateFormulaEvent = new Event('closeAddRateFormulaEvent', {
      bubbles: true
    })
  }

  componentDidMount() {
    if (this.props.forceChanged) this._validateForm()
  }

  _submit = () => {
    // Don't do anything if we have invalid fields or no changes.
    if (!this.state.formValid || !this.state.formChanged) return

    let attributes = {}
    for (let field of this.fields) {
      attributes[field] = this.state[field].value
    }
    this.props.submitCallback(attributes)
    this.setState({ formChanged: false, formSubmitted: true })
  }

  _change = (value, valid, attribute) => {
    let updates = {}
    updates[attribute] = { value: value, valid: valid }

    // Update cost/rate when a formula is chosen
    if (attribute === 'costFormulaId') {
      updates.cost = { value: '', valid: true }
    }
    if (attribute === 'rateFormulaId') {
      updates.rate = { value: '', valid: true }
    }

    this.setState(updates, () => {
      this._checkForChanges()
      this._validateForm()
      this.forceUpdate()
    })
  }

  _setRelationalName = (textField, idField, value) => {
    let change = {}
    change[textField] = { value: value, valid: false }
    change[idField] = { value: '', valid: false }
    this.setState(change, () => {
      this._checkForChanges()
      this._validateForm()
    })
  }

  _checkForChanges = () => {
    let changed = this.props.forceChanged || false
    for (let field of this.fields) {
      let current = this.state[field].value
      let original = this.initialData[field] || ''
      if (current !== original) {
        changed = true
        break
      }
    }
    this.setState({ formChanged: changed }, () => {
      if (this.props.changedCallback) {
        this.props.changedCallback(this.state.formChanged)
      }
    })
  }

  _validateForm = () => {
    let allValid = true
    const skip = [
      'serviceCategoryName',
      'serviceCategoryFormattedName',
      'rateFormulaName',
      'costFormulaName'
    ]
    for (let field of this.fields) {
      if (!includes(skip, field) && !this.state[field].valid) {
        allValid = false
        break
      }
    }
    this.setState({ formValid: allValid })
  }

  _serviceCategoryCreated = serviceCategory => {
    this.setState(
      {
        serviceCategoryName: {
          value: serviceCategory.attributes.name,
          valid: true
        },
        serviceCategoryFormattedName: {
          value: serviceCategory.attributes.formattedName,
          valid: true
        },
        serviceCategoryId: { value: serviceCategory.id, valid: true },
        clearAddServiceCategory: true
      },
      () => {
        this._validateForm()
        setTimeout(
          () => document.dispatchEvent(this.closeAddServiceCategoryEvent),
          500
        )
      }
    )
  }

  _costFormulaCreated = costFormula => {
    this.setState(
      {
        costFormulaName: { value: costFormula.attributes.name, valid: true },
        costFormulaId: { value: costFormula.id, valid: true },
        cost: { value: costFormula.value, valid: true },
        clearAddCostFormula: true
      },
      () => {
        this._validateForm()
        setTimeout(
          () => document.dispatchEvent(this.closeAddCostFormulaEvent),
          500
        )
      }
    )
  }

  _rateFormulaCreated = rateFormula => {
    this.setState(
      {
        rateFormulaName: { value: rateFormula.attributes.name, valid: true },
        rateFormulaId: { value: rateFormula.id, valid: true },
        rate: { value: rateFormula.value, valid: true },
        clearAddRateFormula: true
      },
      () => {
        this._validateForm()
        setTimeout(
          () => document.dispatchEvent(this.closeAddRateFormulaEvent),
          500
        )
      }
    )
  }

  render() {
    return (
      <Card>
        {this.props.formTitle && <CardTitle title={this.props.formTitle} />}
        <CardText>
          <Row>
            <Col lg="4" md="12">
              <RelationalTextInput
                name="serviceCategoryId"
                required={true}
                hintText="Service category (must match, required)"
                floatingLabelText="Service category (must match, required)"
                fullWidth={true}
                openOnFocus={true}
                value={this.state.serviceCategoryFormattedName.value}
                selectedId={this.state.serviceCategoryId.value}
                dataStore={ServiceCategoryStore}
                autoCompleteFilter="nameAutocomplete"
                autoCompleteAttribute="formattedName"
                validatedOnChange={this._change}
                onTextChange={value =>
                  this._setRelationalName(
                    'serviceCategoryFormattedName',
                    'serviceCategoryId',
                    value
                  )
                }
                formCloseEvent={this.closeAddServiceCategoryEvent}
                formComponent={
                  <NewServiceCategory
                    formTitle="Add a new service category"
                    attributes={{ name: this.state.serviceCategoryName.value }}
                    forceChanged={true}
                    savedCallback={this._serviceCategoryCreated}
                  />
                }
              />
            </Col>
            <Col lg="5" md="12">
              <ValidatedTextInput
                name="name"
                required={true}
                errorMessage="Service name is required"
                hintText="Service name"
                floatingLabelText="Service name (required)"
                fullWidth={true}
                value={this.state.name.value}
                validatedOnChange={this._change}
              />
            </Col>
            <Col lg="3" md="12">
              <ValidatedTextInput
                name="rateUnit"
                required={true}
                errorMessage="Unit is required"
                hintText="Hour, Spot, Day..."
                floatingLabelText="Rate unit (required)"
                fullWidth={true}
                value={this.state.rateUnit.value}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>

          <Row>
            <Col md="12" lg="10">
              <TextField
                name="description"
                value={this.state.description.value}
                hintText="Client-facing service name (leave blank to use name)"
                floatingLabelText="Client-facing service name (leave blank to use name)"
                fullWidth
                onChange={(ev, value) =>
                  this._change(value, true, 'description')
                }
              />
            </Col>
            <Col md="12" lg="2">
              <ValidatedTextInput
                name="order"
                errorMessage="Must be a number"
                hintText="1, 2 etc."
                floatingLabelText="Display order"
                fullWidth
                value={this.state.order.value}
                pattern={/^[0-9]+$/}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>

          <Row>
            <Col md="12" lg="12">
              <TextField
                name="notes"
                value={this.state.notes.value}
                hintText="Notes about this service, explanation etc."
                floatingLabelText="Notes about this service, explanation etc."
                multiLine
                fullWidth
                onChange={(ev, value) => this._change(value, true, 'notes')}
              />
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <Subheader
                style={{
                  textAlign: 'center',
                  margin: '40px 0 -5px 0',
                  padding: '0'
                }}
              >
                Pick formulas or add dollar values (formulas have precedence).
                Click the plus signs to add new formulas if needed.
              </Subheader>
            </Col>
          </Row>

          <Row>
            <Col lg="7" md="12">
              <RelationalTextInput
                name="costFormulaId"
                hintText="Cost formula (must match)"
                floatingLabelText="Cost formula (must match)"
                fullWidth={true}
                openOnFocus={true}
                value={this.state.costFormulaName.value}
                selectedId={this.state.costFormulaId.value}
                dataStore={CostFormulaStore}
                autoCompleteFilter="nameAutocomplete"
                autoCompleteAttribute="formattedName"
                validatedOnChange={this._change}
                onTextChange={value =>
                  this._setRelationalName(
                    'costFormulaName',
                    'costFormulaId',
                    value
                  )
                }
                formCloseEvent={this.closeAddCostFormulaEvent}
                formComponent={
                  <NewFormula
                    formTitle="Add a new cost formula"
                    attributes={{
                      name: this.state.costFormulaName.value,
                      formulaType: 'cost'
                    }}
                    forceChanged={true}
                    savedCallback={this._costFormulaCreated}
                  />
                }
              />
            </Col>
            <Col
              lg="1"
              md="12"
              className="mui--text-headline mui--text-dark-hint"
              style={{ textAlign: 'center', marginTop: '32px' }}
            >
              or
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="cost"
                errorMessage="Cost must be a dollar value"
                hintText="123.50"
                floatingLabelText="Service cost ($)"
                fullWidth={true}
                value={this.state.cost.value}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>

          <Row>
            <Col lg="7" md="12">
              <RelationalTextInput
                name="rateFormulaId"
                hintText="Rate formula (must match)"
                floatingLabelText="Rate formula (must match)"
                fullWidth={true}
                openOnFocus={true}
                value={this.state.rateFormulaName.value}
                selectedId={this.state.rateFormulaId.value}
                dataStore={RateFormulaStore}
                autoCompleteFilter="nameAutocomplete"
                autoCompleteAttribute="formattedName"
                validatedOnChange={this._change}
                onTextChange={value =>
                  this._setRelationalName(
                    'rateFormulaName',
                    'rateFormulaId',
                    value
                  )
                }
                formCloseEvent={this.closeAddRateFormulaEvent}
                formComponent={
                  <NewFormula
                    formTitle="Add a new rate formula"
                    attributes={{
                      name: this.state.rateFormulaName.value,
                      formulaType: 'rate'
                    }}
                    forceChanged={true}
                    savedCallback={this._rateFormulaCreated}
                  />
                }
              />
            </Col>
            <Col
              lg="1"
              md="12"
              className="mui--text-headline mui--text-dark-hint"
              style={{ textAlign: 'center', marginTop: '32px' }}
            >
              or
            </Col>
            <Col lg="4" md="12">
              <ValidatedTextInput
                name="rate"
                errorMessage="Rate must be a dollar value"
                hintText="123.50"
                floatingLabelText="Service rate ($)"
                fullWidth={true}
                value={this.state.rate.value}
                validatedOnChange={this._change}
              />
            </Col>
          </Row>
        </CardText>

        <CardActions style={{ textAlign: 'center' }}>
          {this.props.submitCallback && (
            <SaveButton
              type="submit"
              disabled={!this.state.formChanged || !this.state.formValid}
              onTouchTap={this._submit}
            />
          )}
          {this.props.cancelCallback && (
            <CancelButton
              onTouchTap={this.props.cancelCallback}
              disabled={!this.state.formChanged && this.state.formSubmitted}
            />
          )}
        </CardActions>
      </Card>
    )
  }
}

export default ServiceForm



// WEBPACK FOOTER //
// ./src/components/Services/ServiceForm.jsx